import { DragEvent, PointerEvent, TouchEvent } from "react";
import { ResizeHandlerDirection } from "../types";
import { getEmptyImage } from "./getEmptyImage";

export const useResizeHandlers = ({ getContainer, getItem, onEvent }: Params) => {
    function handleDragStart(e: DragEvent) {
        e.stopPropagation();
        e.dataTransfer?.setDragImage(getEmptyImage(), 0, 0);
    }

    const handleDrag = (event: MousePosition, direction: ResizeHandlerDirection) => {
        const container = getContainer();
        if (event.clientY === 0) return;
        if (direction === "n") resizeHandlerN(event, container);
        if (direction === "ne") resizeHandlerNE(event, container);
        if (direction === "e") resizeHandlerE(event, container);
        if (direction === "se") resizeHandlerSE(event, container);
        if (direction === "s") resizeHandlerS(event, container);
        if (direction === "sw") resizeHandlerSW(event, container);
        if (direction === "w") resizeHandlerW(event, container);
        if (direction === "nw") resizeHandlerNW(event, container);
    };

    function resizeHandlerN(mousePosition: MousePosition, container: Container) {
        const item = getItem();
        const [eX, eY] = [
            convertFixedXToRelativeX(mousePosition.clientX, container),
            convertFixedYToRelativeY(mousePosition.clientY, container),
        ];
        const [sX, sY] = calculateSPosition(item);
        const [nX, nY] = calculateNPosition(item);
        const [swX, swY] = calculateSWPosition(item);
        const height = calculateDistanceBetweenALineAndAPoint(
            [
                [swX, swY],
                [sX, sY],
            ],
            [eX, eY],
        );
        const [swXARNew, swYARNew] = calculateSWPositionAR({
            ...item,
            height,
        });
        const left = swX - swXARNew;
        const top = swY - swYARNew;
        if (
            calculateDistanceBetweenTwoPoints([eX, eY], [nX, nY]) >
            calculateDistanceBetweenTwoPoints([eX, eY], [sX, sY])
        )
            return;
        if (height <= 10 && top > item.top) return;
        onEvent({
            left,
            top,
            height,
        });
    }

    function resizeHandlerNE(mousePosition: MousePosition, container: Container) {
        const item = getItem();
        const [eX, eY] = [
            convertFixedXToRelativeX(mousePosition.clientX, container),
            convertFixedYToRelativeY(mousePosition.clientY, container),
        ];
        const [seX, seY] = calculateSEPosition(item);
        const [nwX, nwY] = calculateNWPosition(item);
        const [swX, swY] = calculateSWPosition(item);
        const [neX, neY] = calculateNEPosition(item);
        const width = calculateDistanceBetweenALineAndAPoint(
            [
                [swX, swY],
                [nwX, nwY],
            ],
            [eX, eY],
        );
        const height = calculateDistanceBetweenALineAndAPoint(
            [
                [swX, swY],
                [seX, seY],
            ],
            [eX, eY],
        );
        const [swXARNew, swYARNew] = calculateSWPositionAR({
            ...item,
            width,
            height,
        });
        const left = swX - swXARNew;
        const top = swY - swYARNew;
        if (
            calculateDistanceBetweenTwoPoints([eX, eY], [neX, neY]) >
            calculateDistanceBetweenTwoPoints([eX, eY], [swX, swY])
        )
            return;
        if ((height <= 10 && top > item.top) || width < 0) return;
        onEvent({
            left,
            top,
            width,
            height,
        });
    }

    function resizeHandlerE(mousePosition: MousePosition, container: Container) {
        const item = getItem();
        const [eX, eY] = [
            convertFixedXToRelativeX(mousePosition.clientX, container),
            convertFixedYToRelativeY(mousePosition.clientY, container),
        ];
        const [nwX, nwY] = calculateNWPosition(item);
        const [swX, swY] = calculateSWPosition(item);
        const [peX, peY] = calculateEPosition(item);
        const [wX, wY] = calculateWPosition(item);
        const width = calculateDistanceBetweenALineAndAPoint(
            [
                [swX, swY],
                [nwX, nwY],
            ],
            [eX, eY],
        );
        const [nwXARNew, nwYARNew] = calculateNWPositionAR({
            ...item,
            width,
        });
        const left = nwX - nwXARNew;
        const top = nwY - nwYARNew;
        if (
            calculateDistanceBetweenTwoPoints([eX, eY], [peX, peY]) >
            calculateDistanceBetweenTwoPoints([eX, eY], [wX, wY])
        )
            return;
        if (width < 0) return;
        onEvent({
            width,
            left,
            top,
        });
    }

    function resizeHandlerSE(mousePosition: MousePosition, container: Container) {
        const item = getItem();
        const [eX, eY] = [
            convertFixedXToRelativeX(mousePosition.clientX, container),
            convertFixedYToRelativeY(mousePosition.clientY, container),
        ];
        const [swX, swY] = calculateSWPosition(item);
        const [nwX, nwY] = calculateNWPosition(item);
        const [neX, neY] = calculateNEPosition(item);
        const [seX, seY] = calculateSEPosition(item);
        const width = calculateDistanceBetweenALineAndAPoint(
            [
                [swX, swY],
                [nwX, nwY],
            ],
            [eX, eY],
        );
        const height = calculateDistanceBetweenALineAndAPoint(
            [
                [nwX, nwY],
                [neX, neY],
            ],
            [eX, eY],
        );
        const [nwXARNew, nwYARNew] = calculateNWPositionAR({
            ...item,
            width,
            height,
        });
        const left = nwX - nwXARNew;
        const top = nwY - nwYARNew;
        if (
            calculateDistanceBetweenTwoPoints([eX, eY], [seX, seY]) >
            calculateDistanceBetweenTwoPoints([eX, eY], [neX, neY])
        )
            return;
        if (height <= 10 || (width < 0 && item.width >= 10)) return;
        onEvent({
            left,
            top,
            width,
            height,
        });
    }

    function resizeHandlerS(mousePosition: MousePosition, container: Container) {
        const item = getItem();
        const [eX, eY] = [
            convertFixedXToRelativeX(mousePosition.clientX, container),
            convertFixedYToRelativeY(mousePosition.clientY, container),
        ];
        const [nwX, nwY] = calculateNWPosition(item);
        const [neX, neY] = calculateNEPosition(item);
        const [nX, nY] = calculateNPosition(item);
        const [sX, sY] = calculateSPosition(item);
        const height = calculateDistanceBetweenALineAndAPoint(
            [
                [nwX, nwY],
                [neX, neY],
            ],
            [eX, eY],
        );
        const [nwXARNew, nwYARNew] = calculateNWPositionAR({
            ...item,
            height,
        });
        const left = nwX - nwXARNew;
        const top = nwY - nwYARNew;
        if (
            calculateDistanceBetweenTwoPoints([eX, eY], [sX, sY]) >
            calculateDistanceBetweenTwoPoints([eX, eY], [nX, nY])
        )
            return;
        if (height <= 10) return;
        onEvent({
            left,
            top,
            height,
        });
    }

    function resizeHandlerSW(mousePosition: MousePosition, container: Container) {
        const item = getItem();
        const [eX, eY] = [
            convertFixedXToRelativeX(mousePosition.clientX, container),
            convertFixedYToRelativeY(mousePosition.clientY, container),
        ];
        const [seX, seY] = calculateSEPosition(item);
        const [nwX, nwY] = calculateNWPosition(item);
        const [neX, neY] = calculateNEPosition(item);
        const [swX, swY] = calculateSWPosition(item);
        const width = calculateDistanceBetweenALineAndAPoint(
            [
                [seX, seY],
                [neX, neY],
            ],
            [eX, eY],
        );
        const height = calculateDistanceBetweenALineAndAPoint(
            [
                [nwX, nwY],
                [neX, neY],
            ],
            [eX, eY],
        );
        const [neXARNew, neYARNew] = calculateNEPositionAR({
            ...item,
            width,
            height,
        });
        const left = neX - neXARNew;
        const top = neY - neYARNew;
        if (
            calculateDistanceBetweenTwoPoints([eX, eY], [swX, swY]) >
            calculateDistanceBetweenTwoPoints([eX, eY], [neX, neY])
        )
            return;
        if (height <= 10 || (width < 0 && item.width >= 10)) return;
        onEvent({
            left,
            top,
            width,
            height,
        });
    }

    function resizeHandlerW(mousePosition: MousePosition, container: Container) {
        const item = getItem();
        const [eX, eY] = [
            convertFixedXToRelativeX(mousePosition.clientX, container),
            convertFixedYToRelativeY(mousePosition.clientY, container),
        ];
        const [seX, seY] = calculateSEPosition(item);
        const [neX, neY] = calculateNEPosition(item);
        const [wX, wY] = calculateWPosition(item);
        const [peX, peY] = calculateEPosition(item);
        const width = calculateDistanceBetweenALineAndAPoint(
            [
                [seX, seY],
                [neX, neY],
            ],
            [eX, eY],
        );
        const [neXARNew, neYARNew] = calculateNEPositionAR({
            ...item,
            width,
        });
        const left = neX - neXARNew;
        const top = neY - neYARNew;
        if (
            calculateDistanceBetweenTwoPoints([eX, eY], [wX, wY]) >
            calculateDistanceBetweenTwoPoints([eX, eY], [peX, peY])
        )
            return;
        if (width < 0 && item.width >= 10) return;
        onEvent({
            left,
            top,
            width,
        });
    }

    function resizeHandlerNW(mousePosition: MousePosition, container: Container) {
        const item = getItem();
        const [eX, eY] = [
            convertFixedXToRelativeX(mousePosition.clientX, container),
            convertFixedYToRelativeY(mousePosition.clientY, container),
        ];
        const [neX, neY] = calculateNEPosition(item);
        const [seX, seY] = calculateSEPosition(item);
        const [swX, swY] = calculateSWPosition(item);
        const [nwX, nwY] = calculateNWPosition(item);
        const width = calculateDistanceBetweenALineAndAPoint(
            [
                [neX, neY],
                [seX, seY],
            ],
            [eX, eY],
        );
        const height = calculateDistanceBetweenALineAndAPoint(
            [
                [swX, swY],
                [seX, seY],
            ],
            [eX, eY],
        );
        const [seXARNew, seYARNew] = calculateSEPositionAR({
            ...item,
            width,
            height,
        });
        const left = seX - seXARNew;
        const top = seY - seYARNew;
        if (
            calculateDistanceBetweenTwoPoints([eX, eY], [nwX, nwY]) >
            calculateDistanceBetweenTwoPoints([eX, eY], [seX, seY])
        )
            return;
        if (height <= 10 || (width < 0 && item.width >= 10)) return;
        onEvent({
            left,
            top,
            width,
            height,
        });
    }

    function handleTouchMove(e: TouchEvent, direction: ResizeHandlerDirection) {
        e.stopPropagation();
        const container = getContainer();
        const { clientX, clientY } = e.touches[0];
        if (clientY === 0) return;
        if (direction === "n") resizeHandlerN({ clientX: clientX, clientY: clientY }, container);
        if (direction === "ne") resizeHandlerNE({ clientX: clientX, clientY: clientY }, container);
        if (direction === "e") resizeHandlerE({ clientX: clientX, clientY: clientY }, container);
        if (direction === "se") resizeHandlerSE({ clientX: clientX, clientY: clientY }, container);
        if (direction === "s") resizeHandlerS({ clientX: clientX, clientY: clientY }, container);
        if (direction === "sw") resizeHandlerSW({ clientX: clientX, clientY: clientY }, container);
        if (direction === "w") resizeHandlerW({ clientX: clientX, clientY: clientY }, container);
        if (direction === "nw") resizeHandlerNW({ clientX: clientX, clientY: clientY }, container);
    }

    function onPointerDown(e: PointerEvent) {
        e.stopPropagation();
    }

    return {
        handleDragStart,
        handleDrag,
        handleTouchMove,
        onPointerDown,
    };
};

interface Params {
    getContainer: () => Container;
    getItem: () => Item;
    onEvent: (params: Partial<PositionCalculationParams>) => void;
}

interface Item extends PositionCalculationParams {}

interface Container {
    left: number;
    top: number;
}

interface MousePosition {
    clientX: number;
    clientY: number;
}

interface PositionCalculationParams {
    width: number;
    height: number;
    left: number;
    top: number;
    rotation: number;
}

export type PositionCalculationFunc = (params: PositionCalculationParams) => [number, number];
export type PositionAfterRotationCalculationFunc = (
    params: Omit<PositionCalculationParams, "top" | "left">,
) => [number, number];

/**
 *
 * Trigonometric Formulae
 * x' = x * cos(theta) - y * sin(theta)
 * y' = y * cos(theta) + x * sin(theta)
 *
 */

/**
 *
 * variable naming meaning
 * BR = before rotation
 * AR = after rotation
 * AT = after translation
 */

/**
 *
 * The calculations below are done in an order the browser would do it
 * because here the transform func is: `translate(${left}px, ${top}px) rotate(${rotation}deg)`
 * so the rotation would be done first and then the translation
 *       side note: the benefit of rotating first is that the center movement can be calculated easily
 * in the position calculation functions we are trying to predict what the positions would be
 * based on the formulae given above and according to the order
 * psuedo code:
 *  a. find out the location when the left = 0, top = 0. Here the center would be at width / 2, height / 2 from the top left corner of the container
 *  b. apply the formulae to find out new x (x') and y (y') after rotation
 *  c. adjust the (width / 2 and height / 2) assumptions made. i.e x' = x' + width / 2; y' = height / 2 - y'
 *  d. then add left to x' and top to y'
 *  e. the result would be the rotated and translated value
 *
 */

export const calculateNWPosition: PositionCalculationFunc = ({ width, height, left, top, rotation }) => {
    const [xAR, yAR] = calculateNWPositionAR({ width, height, rotation });
    const [xAT, yAT] = [xAR + left, yAR + top];
    return [xAT, yAT];
};

const calculateNWPositionAR: PositionAfterRotationCalculationFunc = ({ width, height, rotation }) => {
    const theta = convertRotationToThetaRadian(rotation);
    const [xBR, yBR] = [-width / 2, height / 2];
    return [
        xBR * Math.cos(theta) - yBR * Math.sin(theta) + width / 2,
        height / 2 - (yBR * Math.cos(theta) + xBR * Math.sin(theta)),
    ];
};

export const calculateSWPosition: PositionCalculationFunc = ({ width, height, left, top, rotation }) => {
    const [xAR, yAR] = calculateSWPositionAR({ width, height, rotation });
    const [xAT, yAT] = [xAR + left, yAR + top];
    return [xAT, yAT];
};

const calculateSWPositionAR: PositionAfterRotationCalculationFunc = ({ width, height, rotation }) => {
    const theta = convertRotationToThetaRadian(rotation);
    const [xBR, yBR] = [-width / 2, -height / 2];
    return [
        xBR * Math.cos(theta) - yBR * Math.sin(theta) + width / 2,
        height / 2 - (yBR * Math.cos(theta) + xBR * Math.sin(theta)),
    ];
};

export const calculateWPositionAR: PositionAfterRotationCalculationFunc = ({ width, height, rotation }) => {
    const theta = convertRotationToThetaRadian(rotation);
    const [xBR, yBR] = [-width / 2, 0];
    return [
        xBR * Math.cos(theta) - yBR * Math.sin(theta) + width / 2,
        height / 2 - (yBR * Math.cos(theta) + xBR * Math.sin(theta)),
    ];
};

export const calculateWPosition: PositionCalculationFunc = ({ width, height, left, top, rotation }) => {
    const [xAR, yAR] = calculateWPositionAR({ width, height, rotation });
    const [xAT, yAT] = [xAR + left, yAR + top];
    return [xAT, yAT];
};

const calculateEPositionAR: PositionAfterRotationCalculationFunc = ({ width, height, rotation }) => {
    const theta = convertRotationToThetaRadian(rotation);
    const [xBR, yBR] = [width / 2, 0];
    return [
        xBR * Math.cos(theta) - yBR * Math.sin(theta) + width / 2,
        height / 2 - (yBR * Math.cos(theta) + xBR * Math.sin(theta)),
    ];
};

export const calculateEPosition: PositionCalculationFunc = ({ width, height, left, top, rotation }) => {
    const [xAR, yAR] = calculateEPositionAR({ width, height, rotation });
    const [xAT, yAT] = [xAR + left, yAR + top];
    return [xAT, yAT];
};

const calculateNEPositionAR: PositionAfterRotationCalculationFunc = ({ width, height, rotation }) => {
    const theta = convertRotationToThetaRadian(rotation);
    const [xBR, yBR] = [width / 2, height / 2];
    return [
        xBR * Math.cos(theta) - yBR * Math.sin(theta) + width / 2,
        height / 2 - (yBR * Math.cos(theta) + xBR * Math.sin(theta)),
    ];
};

export const calculateNEPosition: PositionCalculationFunc = ({ width, height, left, top, rotation }) => {
    const [xAR, yAR] = calculateNEPositionAR({ width, height, rotation });
    const [xAT, yAT] = [xAR + left, yAR + top];
    return [xAT, yAT];
};

const calculateSPositionAR: PositionAfterRotationCalculationFunc = ({ width, height, rotation }) => {
    const theta = convertRotationToThetaRadian(rotation);
    const [xBR, yBR] = [0, -height / 2];
    return [
        xBR * Math.cos(theta) - yBR * Math.sin(theta) + width / 2,
        height / 2 - (yBR * Math.cos(theta) + xBR * Math.sin(theta)),
    ];
};

export const calculateSPosition: PositionCalculationFunc = ({ width, height, left, top, rotation }) => {
    const [xAR, yAR] = calculateSPositionAR({ width, height, rotation });
    const [xAT, yAT] = [xAR + left, yAR + top];
    return [xAT, yAT];
};

const calculateSEPositionAR: PositionAfterRotationCalculationFunc = ({ width, height, rotation }) => {
    const theta = convertRotationToThetaRadian(rotation);
    const [xBR, yBR] = [width / 2, -height / 2];
    return [
        xBR * Math.cos(theta) - yBR * Math.sin(theta) + width / 2,
        height / 2 - (yBR * Math.cos(theta) + xBR * Math.sin(theta)),
    ];
};

export const calculateSEPosition: PositionCalculationFunc = ({ width, height, left, top, rotation }) => {
    const [xAR, yAR] = calculateSEPositionAR({ width, height, rotation });
    const [xAT, yAT] = [xAR + left, yAR + top];
    return [xAT, yAT];
};

const calculateNPositionAR: PositionAfterRotationCalculationFunc = ({ width, height, rotation }) => {
    const theta = convertRotationToThetaRadian(rotation);
    const [xBR, yBR] = [0, height / 2];
    return [
        xBR * Math.cos(theta) - yBR * Math.sin(theta) + width / 2,
        height / 2 - (yBR * Math.cos(theta) + xBR * Math.sin(theta)),
    ];
};

export const calculateNPosition: PositionCalculationFunc = ({ width, height, left, top, rotation }) => {
    const [xAR, yAR] = calculateNPositionAR({ width, height, rotation });
    const [xAT, yAT] = [xAR + left, yAR + top];
    return [xAT, yAT];
};

export const convertFixedXToRelativeX = (clientX: number, container: Container) => clientX - container.left;

export const convertFixedYToRelativeY = (clientY: number, container: Container) => clientY - container.top;

export const calculateDistanceBetweenTwoPoints = ([x1, y1]: [number, number], [x2, y2]: [number, number]): number =>
    Math.sqrt((x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2));

export const calculateCenterPosition = (item: Item) => {
    const [eX] = calculateEPosition(item);
    const [wX] = calculateWPosition(item);
    const [_nX, nY] = calculateNPosition(item);
    const [_sX, sY] = calculateSPosition(item);
    const [cX, cY] = [wX + (eX - wX) / 2, nY + (sY - nY) / 2];
    return [cX, cY];
};

/**
 *
 * formula: |ax + by + c| / Math.sqrt(a * a + b * b)
 *
 */
const calculateDistanceBetweenALineAndAPoint = (
    line: [[number, number], [number, number]],
    point: [number, number],
) => {
    /**
     * lp = line point
     * rlp = relative line point
     */
    const [[lp1X, lp1Y], [lp2X, lp2Y]] = line;
    const [pX, pY] = point;
    /**
     * let's consider lineP1X, lineP1Y as the center of the axis
     */
    // const [rlp1X, rlp1Y] = [0, 0]; // this is just for demonstration
    const [rlp2X, rlp2Y] = [lp2X - lp1X, lp1Y - lp2Y];
    const [rlpX, rlpY] = [pX - lp1X, lp1Y - pY];
    if (rlp2X === 0) return Math.abs(rlpX); // otherwise gradient would be Infinity
    const gradient = rlp2Y / rlp2X; // as it touches the center
    /**
     * y = mx + c
     * -mx + y - c = 0
     * here m = gradient
     * a = -gradient
     * b = 1
     * c = 0 (as it touches the center)
     * x = rlpX
     * y = rlpY
     */
    const distance = Math.abs(-gradient * rlpX + rlpY) / Math.sqrt(-gradient * -gradient + 1 * 1);
    return distance;
};
export const convertRotationToThetaRadian = (rotation: number) => (rotation === 0 ? 0 : (-rotation * Math.PI) / 180);
